import React from 'react';
import { Helmet } from 'react-helmet';

import type { ImmutableMap } from 'js/src/libs/consts';
import ContentfulTools from 'js/src/libs/contentful-tools';
import type { ContentfulPageType } from 'js/src/libs/contentful-utils';

type Props = {
    pageData: ImmutableMap<ContentfulPageType>;
};

const ContentfulHeader = React.memo(function ContentfulHeader({ pageData }: Props) {
    const helmetContent = [];
    if (pageData.get('structured_data')) {
        helmetContent.push(
            <script type="application/ld+json" key="structured-data">
                {JSON.stringify(pageData.get('structured_data'))}
            </script>,
        );
    }

    if (pageData.get('page_title')) {
        helmetContent.push(<title key="page-title">{pageData.get('page_title')}</title>);
        helmetContent.push(
            <meta property="og:title" content={pageData.get('page_title')} key="og-title" />,
        );
    }

    if (pageData.get('description')) {
        helmetContent.push(
            <meta
                name="description"
                content={pageData.get('description')}
                key="page-description"
            />,
        );
        helmetContent.push(
            <meta
                property="og:description"
                content={pageData.get('description')}
                key="og-description"
            />,
        );
    }

    if (pageData.get('slug')) {
        const url = `https://lumen5.com/${pageData.get('slug')}`;
        helmetContent.push(<meta property="og:url" content={url} key="og-url" />);
        helmetContent.push(<link rel="canonical" href={url} key="canonical-url" />);
    }

    if (pageData.get('image')?.get('file')?.get('url')) {
        helmetContent.push(
            <meta
                property="og:image"
                content={ContentfulTools.getUrl(pageData.get('image').get('file').get('url'), {
                    // https://developers.facebook.com/docs/sharing/best-practices/ says:
                    // "Use images that are at least 1080 pixels in width for best display
                    // on high resolution devices"
                    width: 1200,
                })}
                key="og-image"
            />,
        );
    }
    helmetContent.push(<meta property="og:site_name" content="Lumen5" key="og-site-name" />);
    helmetContent.push(<meta property="og:type" content="website" key="og-type" />);

    return <Helmet>{helmetContent}</Helmet>;
});

export default ContentfulHeader;
